/**
 * Built-in Conditions Registration
 *
 * Registers all built-in condition types and categories with the registry.
 * NO React imports allowed in this file - selectors are registered separately.
 */

import { __ } from '@wordpress/i18n';
import { conditionRegistry } from './condition-registry';
import type {
	UrlConditionValue,
	SpecificPostsValue,
	ArchiveTypeValue,
	TaxonomyTermValue,
	PostAttributeValue,
	ContentTypeValue,
	QueryParamConditionValue,
	UserRoleConditionValue,
	DeviceConditionValue,
	BrowserConditionValue,
	TimeConditionValue,
	DateRangeConditionValue,
	DayConditionValue,
	CookieConditionValue,
	TrafficPercentageValue,
} from '../types/targeting';

// Import evaluators
import {
	evaluateUrlContains,
	evaluateUrlMatches,
	evaluateUrlPath,
	evaluateQueryParam,
	evaluateReferrer,
	evaluateGeolocation,
	evaluateDevice,
	evaluateBrowser,
	evaluateOS,
	evaluateTimeOfDay,
	evaluateDayOfWeek,
	evaluateDateRange,
	evaluateCookie,
	evaluateCustom,
	evaluateTrafficPercentage,
	parseGeolocationCountryCodes,
} from './built-in-evaluators';
import { countryByCode } from '../data/countries';

// ============================================================================
// Register Built-in Categories
// ============================================================================

conditionRegistry.registerCategory({
	id: 'content',
	label: __('Content', 'gp-popups'),
	icon: 'document',
	description: __(
		'Target specific pages, posts, or content types',
		'gp-popups'
	),
	order: 10,
});

conditionRegistry.registerCategory({
	id: 'url',
	label: __('URL & Navigation', 'gp-popups'),
	icon: 'link',
	description: __(
		'Target based on URL patterns and query parameters',
		'gp-popups'
	),
	order: 20,
});

conditionRegistry.registerCategory({
	id: 'user',
	label: __('User & Authentication', 'gp-popups'),
	icon: 'user',
	description: __('Target based on user roles and login status', 'gp-popups'),
	order: 30,
});

conditionRegistry.registerCategory({
	id: 'device',
	label: __('Device & Browser', 'gp-popups'),
	icon: 'device',
	description: __(
		'Target specific devices, browsers, operating systems, or visitor location',
		'gp-popups'
	),
	order: 40,
});

conditionRegistry.registerCategory({
	id: 'time',
	label: __('Time & Schedule', 'gp-popups'),
	icon: 'clock',
	description: __('Target based on time of day or day of week', 'gp-popups'),
	order: 50,
});

conditionRegistry.registerCategory({
	id: 'advanced',
	label: __('Advanced', 'gp-popups'),
	icon: 'cog',
	description: __(
		'Advanced targeting options like cookies and custom rules',
		'gp-popups'
	),
	order: 60,
});

// ============================================================================
// Register Built-in Conditions - Content Category
// ============================================================================

conditionRegistry.register({
	type: 'specific_posts',
	label: __('Specific Posts', 'gp-popups'),
	description: __(
		'Target specific posts, pages, or custom post types',
		'gp-popups'
	),
	icon: 'document-text',
	category: 'content',
	evaluationMode: 'server',
	isCacheable: true,
	supportedOperators: ['is', 'is_not'],
	defaultOperator: 'is',
	defaultValue: { postIds: [] } as SpecificPostsValue,
	helpText: __('Select one or more posts to target', 'gp-popups'),
	formatValue: (value) => {
		const postValue = value as SpecificPostsValue;
		if (!postValue.postIds || postValue.postIds.length === 0) {
			return __('(none selected)', 'gp-popups');
		}
		return `${postValue.postIds.length} ${__('selected', 'gp-popups')}`;
	},
});

conditionRegistry.register({
	type: 'content_type',
	label: __('Content Type', 'gp-popups'),
	description: __(
		'Target based on what kind of page is being viewed',
		'gp-popups'
	),
	icon: 'clipboard',
	category: 'content',
	evaluationMode: 'server',
	isCacheable: true,
	supportedOperators: ['is', 'is_not'],
	defaultOperator: 'is',
	defaultValue: 'page' as ContentTypeValue,
	helpText: __('Select the type of content to target', 'gp-popups'),
	options: {
		front_page: __('Front Page', 'gp-popups'),
		home: __('Blog Home', 'gp-popups'),
		single: __('Single Post', 'gp-popups'),
		page: __('Page', 'gp-popups'),
		singular: __('Any Singular', 'gp-popups'),
		attachment: __('Attachment', 'gp-popups'),
		search: __('Search Results', 'gp-popups'),
		'404': __('404 Page', 'gp-popups'),
		preview: __('Preview', 'gp-popups'),
	},
	formatValue: (value) => {
		const contentType = value as ContentTypeValue;
		return (
			conditionRegistry.getOptionLabel('content_type', contentType) ||
			contentType
		);
	},
});

conditionRegistry.register({
	type: 'archive_type',
	label: __('Archive Type', 'gp-popups'),
	description: __('Target specific archive pages', 'gp-popups'),
	icon: 'archive',
	category: 'content',
	evaluationMode: 'server',
	isCacheable: true,
	supportedOperators: ['is', 'is_not'],
	defaultOperator: 'is',
	defaultValue: { archiveType: 'any' } as ArchiveTypeValue,
	helpText: __('Select the type of archive to target', 'gp-popups'),
	formatValue: (value) => {
		const archiveValue = value as ArchiveTypeValue;
		const labels: Record<string, string> = {
			any: __('Any Archive', 'gp-popups'),
			category: __('Category Archive', 'gp-popups'),
			tag: __('Tag Archive', 'gp-popups'),
			taxonomy: __('Taxonomy Archive', 'gp-popups'),
			author: __('Author Archive', 'gp-popups'),
			post_type_archive: __('Post Type Archive', 'gp-popups'),
			date: __('Date Archive', 'gp-popups'),
			year: __('Year Archive', 'gp-popups'),
			month: __('Month Archive', 'gp-popups'),
			day: __('Day Archive', 'gp-popups'),
		};
		return labels[archiveValue.archiveType] || archiveValue.archiveType;
	},
});

conditionRegistry.register({
	type: 'taxonomy_term',
	label: __('Taxonomy Term', 'gp-popups'),
	description: __(
		'Target content with specific categories, tags, or terms',
		'gp-popups'
	),
	icon: 'tag',
	category: 'content',
	evaluationMode: 'server',
	isCacheable: true,
	supportedOperators: ['has', 'has_not'],
	defaultOperator: 'has',
	defaultValue: { taxonomy: 'category', terms: [] } as TaxonomyTermValue,
	helpText: __('Select taxonomy terms to target', 'gp-popups'),
	formatValue: (value) => {
		const termValue = value as TaxonomyTermValue;
		if (!termValue.terms || termValue.terms.length === 0) {
			return __('(none selected)', 'gp-popups');
		}
		return `${termValue.terms.length} ${__('terms selected', 'gp-popups')}`;
	},
});

conditionRegistry.register({
	type: 'post_attribute',
	label: __('Post Attribute', 'gp-popups'),
	description: __(
		'Target based on post attributes like template, parent, or custom fields',
		'gp-popups'
	),
	icon: 'settings',
	category: 'content',
	evaluationMode: 'server',
	isCacheable: true,
	supportedOperators: [
		'is',
		'is_not',
		'contains',
		'not_contains',
		'exists',
		'not_exists',
	],
	defaultOperator: 'is',
	defaultValue: { attribute: 'sticky' } as PostAttributeValue,
	helpText: __('Select a post attribute to check', 'gp-popups'),
	formatValue: (value) => {
		const attrValue = value as PostAttributeValue;
		const labels: Record<string, string> = {
			paginated: __('Paginated', 'gp-popups'),
			sticky: __('Sticky Post', 'gp-popups'),
			page_template: __('Page Template', 'gp-popups'),
			has_parent: __('Has Parent', 'gp-popups'),
			is_child: __('Is Child Page', 'gp-popups'),
			custom_field: __('Custom Field', 'gp-popups'),
		};
		return labels[attrValue.attribute] || attrValue.attribute;
	},
});

conditionRegistry.register({
	type: 'post_type',
	label: __('Post Type', 'gp-popups'),
	description: __(
		'Target content belonging to specific post types (posts, pages, or custom post types)',
		'gp-popups'
	),
	icon: 'folder',
	category: 'content',
	evaluationMode: 'server',
	isCacheable: true,
	supportedOperators: ['is', 'is_not'],
	defaultOperator: 'is',
	defaultValue: [] as string[],
	helpText: __('Match based on the current post type', 'gp-popups'),
	formatValue: (value) => {
		// Support both legacy string and new array format
		if (typeof value === 'string') {
			return value || __('(none selected)', 'gp-popups');
		}
		const postTypes = value as string[];
		if (!postTypes || postTypes.length === 0) {
			return __('(none selected)', 'gp-popups');
		}
		if (postTypes.length === 1) {
			return postTypes[0];
		}
		return `${postTypes.length} ${__('types selected', 'gp-popups')}`;
	},
});

// ============================================================================
// Register Built-in Conditions - URL Category
// ============================================================================

conditionRegistry.register({
	type: 'url_contains',
	label: __('URL Contains', 'gp-popups'),
	description: __('Target URLs containing specific text', 'gp-popups'),
	icon: 'search',
	category: 'url',
	evaluationMode: 'client',
	supportedOperators: ['contains', 'not_contains'],
	defaultOperator: 'contains',
	defaultValue: { pattern: '', caseSensitive: false },
	placeholder: '/products/',
	helpText: __('Enter text that should appear in the URL', 'gp-popups'),
	evaluate: evaluateUrlContains,
	formatValue: (value) => {
		const urlValue = value as UrlConditionValue;
		return urlValue.pattern
			? `"${urlValue.pattern}"`
			: __('(empty)', 'gp-popups');
	},
});

conditionRegistry.register({
	type: 'url_matches',
	label: __('URL Pattern (Regex)', 'gp-popups'),
	description: __('Target URLs matching a regex pattern', 'gp-popups'),
	icon: 'regex',
	category: 'url',
	evaluationMode: 'client',
	supportedOperators: ['matches_regex'],
	defaultOperator: 'matches_regex',
	defaultValue: { pattern: '', caseSensitive: false },
	placeholder: '^/blog/[0-9]+',
	helpText: __('Enter a regular expression pattern', 'gp-popups'),
	evaluate: evaluateUrlMatches,
	formatValue: (value) => {
		const urlValue = value as UrlConditionValue;
		return urlValue.pattern
			? `"${urlValue.pattern}"`
			: __('(empty)', 'gp-popups');
	},
});

conditionRegistry.register({
	type: 'url_path',
	label: __('URL Path', 'gp-popups'),
	description: __('Target specific URL paths', 'gp-popups'),
	icon: 'route',
	category: 'url',
	evaluationMode: 'client',
	supportedOperators: ['is', 'is_not', 'starts_with', 'ends_with'],
	defaultOperator: 'is',
	defaultValue: { pattern: '/', caseSensitive: false },
	placeholder: '/shop/category/',
	helpText: __('Enter the URL path (e.g., /about-us/)', 'gp-popups'),
	evaluate: evaluateUrlPath,
	formatValue: (value) => {
		const urlValue = value as UrlConditionValue;
		return urlValue.pattern
			? `"${urlValue.pattern}"`
			: __('(empty)', 'gp-popups');
	},
});

conditionRegistry.register({
	type: 'query_param',
	label: __('Query Parameter', 'gp-popups'),
	description: __('Target URLs with specific query parameters', 'gp-popups'),
	icon: 'question',
	category: 'url',
	evaluationMode: 'client',
	supportedOperators: ['is', 'is_not', 'contains', 'exists', 'not_exists'],
	defaultOperator: 'exists',
	defaultValue: { paramName: '', paramValue: '' },
	placeholder: 'utm_source',
	helpText: __('Target visitors with specific URL parameters', 'gp-popups'),
	evaluate: evaluateQueryParam,
	formatValue: (value, operator) => {
		const queryValue = value as QueryParamConditionValue;
		if (!queryValue.paramName) {
			return __('(empty)', 'gp-popups');
		}
		if (operator === 'exists' || operator === 'not_exists') {
			return `"${queryValue.paramName}"`;
		}
		if (queryValue.paramValue) {
			return `${queryValue.paramName}="${queryValue.paramValue}"`;
		}
		return `"${queryValue.paramName}"`;
	},
});

conditionRegistry.register({
	type: 'referrer',
	label: __('Referrer', 'gp-popups'),
	description: __(
		'Target visitors from specific referral sources',
		'gp-popups'
	),
	icon: 'arrow-back',
	category: 'url',
	evaluationMode: 'client',
	supportedOperators: ['is', 'is_not', 'contains', 'starts_with'],
	defaultOperator: 'contains',
	defaultValue: { pattern: '', caseSensitive: false },
	placeholder: 'google.com',
	helpText: __('Target visitors coming from specific websites', 'gp-popups'),
	evaluate: evaluateReferrer,
	formatValue: (value) => {
		const urlValue = value as UrlConditionValue;
		return urlValue.pattern
			? `"${urlValue.pattern}"`
			: __('(empty)', 'gp-popups');
	},
});

// ============================================================================
// Register Built-in Conditions - User Category
// ============================================================================

conditionRegistry.register({
	type: 'user_role',
	label: __('User Role', 'gp-popups'),
	description: __('Target users with specific roles', 'gp-popups'),
	icon: 'user-group',
	category: 'user',
	evaluationMode: 'server',
	isCacheable: false, // User-dependent, requires AJAX
	supportedOperators: ['is', 'is_not'],
	defaultOperator: 'is',
	defaultValue: { roles: [] },
	helpText: __('Select user roles to target', 'gp-popups'),
	options: {
		administrator: __('Administrator', 'gp-popups'),
		editor: __('Editor', 'gp-popups'),
		author: __('Author', 'gp-popups'),
		contributor: __('Contributor', 'gp-popups'),
		subscriber: __('Subscriber', 'gp-popups'),
	},
	formatValue: (value) => {
		const roleValue = value as UserRoleConditionValue;
		if (!roleValue.roles || roleValue.roles.length === 0) {
			return __('(none selected)', 'gp-popups');
		}
		return roleValue.roles
			.map((r) => conditionRegistry.getOptionLabel('user_role', r))
			.join(', ');
	},
});

conditionRegistry.register({
	type: 'user_logged_in',
	label: __('Login Status', 'gp-popups'),
	description: __('Target logged in or logged out users', 'gp-popups'),
	icon: 'lock',
	category: 'user',
	evaluationMode: 'server',
	isCacheable: false, // User-dependent, requires AJAX
	supportedOperators: ['is'],
	defaultOperator: 'is',
	defaultValue: 'logged_in',
	helpText: __('Target based on whether user is logged in', 'gp-popups'),
	options: {
		logged_in: __('Logged In', 'gp-popups'),
		logged_out: __('Logged Out', 'gp-popups'),
	},
	formatValue: (value) => {
		const loginValue = value as string;
		return conditionRegistry.getOptionLabel('user_logged_in', loginValue);
	},
});

// ============================================================================
// Register Built-in Conditions - Device Category
// ============================================================================

conditionRegistry.register({
	type: 'device',
	label: __('Device Type', 'gp-popups'),
	description: __('Target specific device types', 'gp-popups'),
	icon: 'phone',
	category: 'device',
	evaluationMode: 'client',
	supportedOperators: ['is', 'is_not'],
	defaultOperator: 'is',
	defaultValue: { devices: [] },
	helpText: __('Select device types to target', 'gp-popups'),
	evaluate: evaluateDevice,
	options: {
		desktop: __('Desktop', 'gp-popups'),
		tablet: __('Tablet', 'gp-popups'),
		mobile: __('Mobile', 'gp-popups'),
	},
	formatValue: (value) => {
		const deviceValue = value as DeviceConditionValue;
		if (!deviceValue.devices || deviceValue.devices.length === 0) {
			return __('(none selected)', 'gp-popups');
		}
		return deviceValue.devices
			.map((d) => conditionRegistry.getOptionLabel('device', d))
			.join(', ');
	},
});

conditionRegistry.register({
	type: 'browser',
	label: __('Browser', 'gp-popups'),
	description: __('Target specific web browsers', 'gp-popups'),
	icon: 'globe',
	category: 'device',
	evaluationMode: 'client',
	supportedOperators: ['is', 'is_not'],
	defaultOperator: 'is',
	defaultValue: { browsers: [] },
	helpText: __('Select browsers to target', 'gp-popups'),
	evaluate: evaluateBrowser,
	options: {
		chrome: 'Chrome',
		firefox: 'Firefox',
		safari: 'Safari',
		edge: 'Edge',
		opera: 'Opera',
		ie: 'Internet Explorer',
	},
	formatValue: (value) => {
		const browserValue = value as BrowserConditionValue;
		if (!browserValue.browsers || browserValue.browsers.length === 0) {
			return __('(none selected)', 'gp-popups');
		}
		return browserValue.browsers
			.map((b) => conditionRegistry.getOptionLabel('browser', b))
			.join(', ');
	},
});

conditionRegistry.register({
	type: 'os',
	label: __('Operating System', 'gp-popups'),
	description: __('Target specific operating systems', 'gp-popups'),
	icon: 'computer',
	category: 'device',
	evaluationMode: 'client',
	supportedOperators: ['is', 'is_not'],
	defaultOperator: 'is',
	defaultValue: '',
	helpText: __('Select operating systems to target', 'gp-popups'),
	evaluate: evaluateOS,
	options: {
		windows: 'Windows',
		macos: 'macOS',
		linux: 'Linux',
		ios: 'iOS',
		android: 'Android',
	},
	formatValue: (value) => {
		const osValue = value as string;
		return (
			conditionRegistry.getOptionLabel('os', osValue) ||
			osValue ||
			__('(none selected)', 'gp-popups')
		);
	},
});

conditionRegistry.register({
	type: 'geolocation',
	label: __('Geolocation', 'gp-popups'),
	description: __(
		'Target visitors by country based on IP address',
		'gp-popups'
	),
	icon: 'globe',
	category: 'device',
	evaluationMode: 'client',
	supportedOperators: ['is', 'is_not'],
	defaultOperator: 'is',
	defaultValue: [] as string[],
	helpText: __('Select the countries to target.', 'gp-popups'),
	evaluate: evaluateGeolocation,
	formatValue: (value) => {
		const codes = parseGeolocationCountryCodes(value);
		if (codes.length === 0) {
			return __('(none selected)', 'gp-popups');
		}
		return codes.map((code) => countryByCode[code] || code).join(', ');
	},
});

// ============================================================================
// Register Built-in Conditions - Time Category
// ============================================================================

conditionRegistry.register({
	type: 'time_of_day',
	label: __('Time of Day', 'gp-popups'),
	description: __('Target specific hours of the day', 'gp-popups'),
	icon: 'clock-time',
	category: 'time',
	evaluationMode: 'client',
	supportedOperators: ['between'],
	defaultOperator: 'between',
	defaultValue: { startTime: '09:00', endTime: '17:00', timezone: 'local' },
	helpText: __('Set the time range when popup should appear', 'gp-popups'),
	evaluate: evaluateTimeOfDay,
	formatValue: (value) => {
		const timeValue = value as TimeConditionValue;
		if (!timeValue.startTime || !timeValue.endTime) {
			return __('(not configured)', 'gp-popups');
		}
		return `${timeValue.startTime} – ${timeValue.endTime}`;
	},
});

conditionRegistry.register({
	type: 'day_of_week',
	label: __('Day of Week', 'gp-popups'),
	description: __('Target specific days of the week', 'gp-popups'),
	icon: 'calendar-days',
	category: 'time',
	evaluationMode: 'client',
	supportedOperators: ['is', 'is_not'],
	defaultOperator: 'is',
	defaultValue: { days: [] },
	helpText: __('Select days when popup should appear', 'gp-popups'),
	evaluate: evaluateDayOfWeek,
	options: {
		monday: __('Mon', 'gp-popups'),
		tuesday: __('Tue', 'gp-popups'),
		wednesday: __('Wed', 'gp-popups'),
		thursday: __('Thu', 'gp-popups'),
		friday: __('Fri', 'gp-popups'),
		saturday: __('Sat', 'gp-popups'),
		sunday: __('Sun', 'gp-popups'),
	},
	formatValue: (value) => {
		const dayValue = value as DayConditionValue;
		if (!dayValue.days || dayValue.days.length === 0) {
			return __('(none selected)', 'gp-popups');
		}
		return dayValue.days
			.map((d) => conditionRegistry.getOptionLabel('day_of_week', d))
			.join(', ');
	},
});

conditionRegistry.register({
	type: 'date_range',
	label: __('Date Range', 'gp-popups'),
	description: __('Target specific date ranges', 'gp-popups'),
	icon: 'calendar',
	category: 'time',
	evaluationMode: 'client',
	supportedOperators: ['between'],
	defaultOperator: 'between',
	defaultValue: {
		startDate: new Date().toISOString().split('T')[0],
		endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
			.toISOString()
			.split('T')[0],
		timezone: 'local',
	},
	helpText: __('Set the date range when popup should appear', 'gp-popups'),
	evaluate: evaluateDateRange,
	formatValue: (value) => {
		const dateValue = value as DateRangeConditionValue;
		if (!dateValue.startDate || !dateValue.endDate) {
			return __('(not configured)', 'gp-popups');
		}
		return `${dateValue.startDate} – ${dateValue.endDate}`;
	},
});

// ============================================================================
// Register Built-in Conditions - Advanced Category
// ============================================================================

conditionRegistry.register({
	type: 'cookie',
	label: __('Cookie', 'gp-popups'),
	description: __('Target based on browser cookies', 'gp-popups'),
	icon: 'cookie',
	category: 'advanced',
	evaluationMode: 'client',
	supportedOperators: ['is', 'is_not', 'contains', 'exists', 'not_exists'],
	defaultOperator: 'exists',
	defaultValue: { cookieName: '', cookieValue: '' },
	placeholder: 'my_cookie',
	helpText: __('Target visitors with specific cookies', 'gp-popups'),
	evaluate: evaluateCookie,
	formatValue: (value, operator) => {
		const cookieValue = value as CookieConditionValue;
		if (!cookieValue.cookieName) {
			return __('(empty)', 'gp-popups');
		}
		if (operator === 'exists' || operator === 'not_exists') {
			return `"${cookieValue.cookieName}"`;
		}
		if (cookieValue.cookieValue) {
			return `${cookieValue.cookieName}="${cookieValue.cookieValue}"`;
		}
		return `"${cookieValue.cookieName}"`;
	},
});

conditionRegistry.register({
	type: 'custom',
	label: __('Custom JavaScript', 'gp-popups'),
	description: __(
		'Use custom JavaScript for advanced targeting',
		'gp-popups'
	),
	icon: 'code',
	category: 'advanced',
	evaluationMode: 'client',
	supportedOperators: ['is'],
	defaultOperator: 'is',
	defaultValue: 'return true;',
	placeholder: 'return window.myCondition === true;',
	helpText: __('Write JavaScript that returns true/false', 'gp-popups'),
	evaluate: evaluateCustom,
	formatValue: (value) => {
		const customValue = value as string;
		if (!customValue || customValue === 'return true;') {
			return __('(default)', 'gp-popups');
		}
		// Truncate long custom code
		const truncated =
			customValue.length > 30
				? customValue.substring(0, 30) + '...'
				: customValue;
		return truncated;
	},
});

conditionRegistry.register({
	type: 'traffic_percentage',
	label: __('Traffic Percentage', 'gp-popups'),
	description: __(
		'Show to a percentage of visitors for A/B testing',
		'gp-popups'
	),
	icon: 'dice',
	category: 'advanced',
	evaluationMode: 'client',
	supportedOperators: ['is'],
	defaultOperator: 'is',
	defaultValue: { min: 1, max: 100 } as TrafficPercentageValue,
	helpText: __(
		'Define a visitor range (1–100) for A/B testing. Use non–overlapping ranges like 1–50 for Variant A and 51–100 for Variant B.',
		'gp-popups'
	),
	evaluate: evaluateTrafficPercentage,
	formatValue: (value) => {
		const rangeValue = value as TrafficPercentageValue;
		const percentage = rangeValue.max - rangeValue.min + 1;
		return `${rangeValue.min}-${rangeValue.max} (${percentage}%)`;
	},
});
